import React, { useCallback, useEffect, useState } from "react";
import { Modal } from "../../widgets/modal";
import TextField from "../../widgets/textField/textField";
import {
  BENCHMARK_SINGLEREQUESTEDCHANGE,
  BENCHMARK_SCHEDULINGPRODUCTS,
  DATE_FORMAT_UPDATED,
} from "../../constants/app.constants";
import {
  updateSingleRequestedChangeScheduled,
  fetchRequestedChangeScheduled,
  fetchBenchmarkProducts,
} from "./scheduling.utils";
import { useQuery } from "@tanstack/react-query";
import { FC } from "react";
import Checkbox from "../../widgets/checkbox";
import DropdownList from "../../widgets/DropdownList";
import Swal from "sweetalert2";
import Loader from "../../widgets/Loader";
import TextArea from "../../widgets/textArea";
import { SCHEDULING_DIALOGS } from "../../constants/dialog.constants";
// import DatePicker from "@mui/x-date-pickers/DatePicker"; // MUI v6 new picker
// import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
// import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
// import dayjs, { Dayjs } from "dayjs";
// import { dateFormat } from "../../constants/app.constants";

import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers"; // Correct named import
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs, { Dayjs } from "dayjs";


type OverviewModalProps = {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: () => void;
  requestChangeId: number;
};

// Accept both Date and Dayjs
type UpdatedValue = string[] | string | Dayjs | Date | null | undefined;

const SchedulingRequestChangeModal: FC<OverviewModalProps> = (props) => {
  const { requestChangeId, isOpen, onClose, onSubmit } = props;
  const [requestedDeliveryDate, setRequestedDeliveryDate] = useState<Dayjs | null>(null);
  const [sensitiveClient, setSensitiveClient] = useState(0);
  const [requestedYardage, setRequestedYardage] = useState("");
  const [requestedProduct, setRequestedProduct] = useState("");
  const [note, setNote] = useState("");
  const [isShowLoader, setIsShowLoader] = useState(false);
  const [validationMsg, setValidationMsg] = useState("");

  const { isFetching, data: dataBenchmarkSingleRequestedChange } = useQuery({
    queryKey: [BENCHMARK_SINGLEREQUESTEDCHANGE, requestChangeId],
    queryFn: () => fetchRequestedChangeScheduled(requestChangeId),
    enabled: isOpen,
    refetchOnWindowFocus: false,
  });

  const {
    isLoading: isLoadingBenchmarkProducts,
    error: errorBenchmarkProducts,
    data: dataBenchmarkProducts,
    isFetching: isFetchingBenchmarkProducts,
  } = useQuery({
    queryKey: [BENCHMARK_SCHEDULINGPRODUCTS],
    queryFn: () => fetchBenchmarkProducts(),
    enabled: isOpen,
    refetchOnWindowFocus: false,
  });

  // Update handler with extra params and support for Dayjs/Date
  const onChangeFieldValues = useCallback(
    (
      id: string,
      updatedValue?: UpdatedValue,
      updatedName?: string,
      parsedObj?: string,
      rowAndFieldIndex?: string
    ) => {
      if (id === "requestedDeliveryDate") {
        if (updatedValue instanceof Date) {
          setRequestedDeliveryDate(dayjs(updatedValue));
        } else if (dayjs.isDayjs(updatedValue)) {
          setRequestedDeliveryDate(updatedValue);
        } else {
          setRequestedDeliveryDate(null);
        }
       } else if (id === "requestedYardage") {
        setRequestedYardage(updatedValue as string);
      } else if (id === "requestedProduct") {
        setRequestedProduct((updatedValue as string) || "");
      } else if (id === "note") {
        setNote((updatedValue as string) || "");
      }
    },
    []
  );

  useEffect(() => {
    if (requestChangeId && !isFetching && isOpen === true) {
      // Convert Date to Dayjs for picker
      setRequestedDeliveryDate(
        dataBenchmarkSingleRequestedChange?.RequestedDeliveryDate
          ? dayjs(dataBenchmarkSingleRequestedChange.RequestedDeliveryDate)
          : null
      );
      setRequestedYardage(dataBenchmarkSingleRequestedChange?.RequestedYardage || "");
      setRequestedProduct(dataBenchmarkSingleRequestedChange?.RequestedProduct || "");
      setNote(dataBenchmarkSingleRequestedChange?.Note || "");
      setSensitiveClient(dataBenchmarkSingleRequestedChange?.SensitiveClient || 0);
      setIsShowLoader(false);
    }
  }, [dataBenchmarkSingleRequestedChange, requestChangeId, isFetching, isOpen]);

  const handleCheckbox = (id: string, isChecked: boolean) => {
    if (id === "sensitiveClient") {
      setSensitiveClient(isChecked ? 1 : 0);
    }
  };

  const handleSubmit = useCallback(() => {
    if (!requestedDeliveryDate) {
      setValidationMsg("Please select requested Delivery date.");
      return;
    }
    if (requestedYardage.trim() === "") {
      setValidationMsg("Please enter requested Yardage.");
      return;
    }
    if (requestedProduct.trim() === "") {
      setValidationMsg("Please select requested Product.");
      return;
    }

    if (requestChangeId) {
      setIsShowLoader(true);
      updateSingleRequestedChangeScheduled(
        requestChangeId,
        requestedYardage,
        requestedProduct,
        note,
        requestedDeliveryDate.toDate(),
        sensitiveClient
      ).then((updateResult) => {
        setIsShowLoader(false);
        setValidationMsg("");
        if (updateResult) {
          Swal.fire({
            customClass: {
              popup: "sweetAlert",
            },
            icon: "success",
            showConfirmButton: false,
            timer: 1500,
            title: SCHEDULING_DIALOGS.requestSentSuccess,
          });
          // onClose();
          onSubmit();
        } else {
          Swal.fire({
            customClass: ".swal2-confirm",
            icon: "warning",
            title: SCHEDULING_DIALOGS.requestSendFailed,
          });
        }
      });
    }
  }, [
    requestedDeliveryDate,
    requestedYardage,
    requestedProduct,
    note,
    sensitiveClient,
    requestChangeId,
    // onClose,
    onSubmit
  ]);

  const handleCancel = useCallback(() => {
    setRequestedDeliveryDate(null);
    setSensitiveClient(0);
    setRequestedYardage("");
    setRequestedProduct("");
    setNote("");
    setIsShowLoader(false);
    onClose();
    setValidationMsg("");
  }, [onClose]);

  return (
    <Modal show={isOpen} label="Request Change" onClose={handleCancel}>
      {validationMsg !== "" && <div className="text-danger">{validationMsg}</div>}
      <div className="requestModalSection">
        <div className="modalForm">
          {(isShowLoader || isFetching) && <Loader />}
          <form>
            <div className="modal-body">
              <div className="form-group row mb-3">
                <label className="col-md-4 col-form-label">Sensitive Client:</label>
                <div className="col-md-8 mt-2">
                  <Checkbox
                    id="sensitiveClient"
                    isChecked={!!sensitiveClient}
                    onChange={handleCheckbox}
                  />
                </div>
              </div>

              <div className="form-group row mb-3">
                <label className="col-md-4 col-form-label">
                  Requested Delivery Date:<span className="text-danger">*</span>
                </label>
                <div className="col-md-8">
               
              <LocalizationProvider dateAdapter={AdapterDayjs}>
  <DatePicker
    value={requestedDeliveryDate}
    onChange={(newDate: Dayjs | null) =>
      onChangeFieldValues("requestedDeliveryDate", newDate ?? null)
    }
    format={DATE_FORMAT_UPDATED}
     slotProps={{
                        textField: {
                          size: "small",
                          className: "date-input-field"
                        },
                      }}
  />
</LocalizationProvider>
  </div>
              </div>

              <div className="form-group row mb-3">
                <label className="col-md-4 col-form-label">
                  Requested Yardage:<span className="text-danger">*</span>
                </label>
                <div className="col-md-2">
                  <TextField
                    id="requestedYardage"
                    onChange={onChangeFieldValues}
                    type="text"
                    defaultValue={requestedYardage}
                    className="form-control input-sm"
                  />
                </div>
              </div>

              <div className="form-group row mb-3">
                <label className="col-md-4 col-form-label">
                  Requested Product:<span className="text-danger">*</span>
                </label>
                <div className="col-md-8">
                  <DropdownList
                    id="requestedProduct"
                    dropDownData={dataBenchmarkProducts}
                    onChange={onChangeFieldValues}
                    defaultOption={"Select Product"}
                    selectedID={requestedProduct}
                    className="form-select"
                  />
                </div>
              </div>

              <div className="form-group row mb-3">
                <label className="col-md-4 col-form-label">Note:</label>
                <div className="col-md-8">
                  <TextArea
                    id="note"
                    className="form-control textareaInput"
                    onChange={onChangeFieldValues}
                    value={note}
                    rows={5}
                  />
                </div>
              </div>

              <div className="form-group row">
                <div className="col-md-4"></div>
                <div className="col-md-8 actionButtons">
                  <button
                    type="button"
                    className="btn cancelPwdBtn mb-3"
                    onClick={handleCancel}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="btn submitBtn mb-3"
                    onClick={handleSubmit}
                  >
                    Submit
                  </button>
                </div>
              </div>
            </div>
          </form>
        </div>
      </div>
    </Modal>
  );
};

export default SchedulingRequestChangeModal;
